import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { PencilSquareIcon, ChartBarIcon } from "@heroicons/react/16/solid";
import { SpeakerWaveIcon, CloudArrowUpIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import type { DraggableProvided } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import { checkModuleSlug } from "../../../api/client";
import { useSlugValidation } from "../../../hooks/useSlugValidation";
import { useUpdateTopic } from "../../../api/mutations";
import SlugInputField from "../../../componentLibrary/SlugInputField";
import {
  CategoryEnum,
  ImageI,
  ModuleI,
  ModuleStatus,
  LessonI,
} from "../../../types";
import MultiSelectDropdown from "../../../componentLibrary/MultiSelectDropdown";
import Container from "../../../componentLibrary/Container";
import Button from "../../../componentLibrary/Button";
import Input from "../../../componentLibrary/Input";
import Card from "../../../componentLibrary/Card";
import { useNotification } from "../../../hooks/useNotification";
import validateModule from "../utils/validateTopic";
import Select, { SelectOption } from "../../../componentLibrary/Select";
import { ImageTypeEnum } from "../../../types";
import BookmarkButton from "../../../components/BookmarkButton";
import ExternalTextEditor from "../../../componentLibrary/ExternalTextEditor";
import TabGroup from "../../../componentLibrary/TabGroup";
import LessonFeedbackModal from "../../../components/LessonFeedbackModal";
import { useGetLessonFeedbackCount } from "../../../api/queries";
import AddImageModal from "../../../components/AddImageModal";
import ReorderableVerticalList from "../../../componentLibrary/ReorderableVerticalList";
import RbdFullRowDragSurface from "../../../componentLibrary/RbdFullRowDragSurface";
import IconSelect from "./IconSelect";
import LessonSelect from "./LessonSelect";
import ColorSelect from "./ColorSelect";
import ThumbnailSelect from "./ThumbnailSelect";

interface LessonCardProps {
  lesson: LessonI;
  onOpenFeedbackModal: (lessonId: string, lessonTitle: string) => void;
  moduleId: string;
  provided: DraggableProvided;
  canUpdateContent?: boolean;
}

const LessonCard: React.FC<LessonCardProps> = ({
  lesson,
  onOpenFeedbackModal,
  moduleId,
  provided,
  canUpdateContent = true,
}) => {
  const navigate = useNavigate();
  const { data: lessonFeedbackCounts } = useGetLessonFeedbackCount(lesson._id);
  const feedbackTotal = lessonFeedbackCounts?.total ?? 0;
  const feedbackUnresolved = lessonFeedbackCounts?.unresolved ?? 0;
  const hasFeedback = feedbackTotal > 0;
  const allFeedbackResolved = hasFeedback && feedbackUnresolved === 0;

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      style={{
        ...provided.draggableProps.style,
        userSelect: "none",
      }}
    >
      <div className="relative">
        <Button
          variant="alternative"
          onClick={() => onOpenFeedbackModal(lesson._id, lesson.title)}
          title={
            hasFeedback
              ? allFeedbackResolved
                ? `All feedback resolved (${feedbackTotal} total). Open to review.`
                : `View lesson feedback (${feedbackUnresolved} unresolved of ${feedbackTotal})`
              : "No feedback yet"
          }
          className={`absolute top-2 right-2 z-10 ${!hasFeedback ? "opacity-40 cursor-not-allowed" : ""}`}
          disabled={!hasFeedback}
        >
          <ChartBarIcon
            className={`w-6 h-6 ${
              !hasFeedback
                ? "text-gray"
                : allFeedbackResolved
                  ? "text-electrum-200"
                  : "text-electrum"
            }`}
          />
        </Button>
        <Card
          disabled={false}
          title={
            <div className="flex flex-row space-x-2 items-center">
              <Button
                variant="alternative"
                onClick={() =>
                  canUpdateContent &&
                  navigate(`/admin/edit-lesson/${lesson._id}`, {
                    state: { moduleId },
                  })
                }
                disabled={!canUpdateContent}
                title={
                  !canUpdateContent
                    ? "Ask an admin if you need permission to edit lessons"
                    : "Edit lesson"
                }
              >
                <PencilSquareIcon className="w-6 h-6" />
              </Button>
              <Button
                variant="alternative"
                onClick={() =>
                  canUpdateContent &&
                  navigate(`/teleprompters/${lesson._id}`, {
                    state: { moduleId },
                  })
                }
                disabled={!canUpdateContent}
                title={
                  !canUpdateContent
                    ? "Ask an admin if you need permission to access teleprompter"
                    : "Open Teleprompter"
                }
              >
                <SpeakerWaveIcon className="w-6 h-6" />
              </Button>
              <div className="text-md font-semibold">{lesson.title}</div>
            </div>
          }
        />
      </div>
    </div>
  );
};

const blankModule = {
  _id: "",
  title: "",
  subtitle: "",
  description: "",
  color: "#e1395b",
  icon: {
    _id: "",
    src: "",
    key: "",
    name: "",
    alt: "",
    type: ImageTypeEnum.ICON,
  },
  thumbnail: null,
  lessons: [],
  categories: [],
  status: ModuleStatus.Draft,
  trailerVideoUrl: "",
  trailerTranscript: "",
};

const EditTopicComponent = ({
  originalModuleData,
  isEdit,
  setIsEdit,
  canUpdateContent = true,
  canCreateContent = false,
}: {
  originalModuleData: ModuleI;
  isEdit: boolean;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
  canUpdateContent?: boolean;
  canCreateContent?: boolean;
}) => {
  const [updatedModule, setUpdatedModule] = useState<ModuleI>(blankModule);
  const {
    status: slugStatus,
    validate: validateSlug,
    reset: resetSlugStatus,
  } = useSlugValidation(checkModuleSlug, updatedModule._id);

  // Upload image modal: which type (icon vs thumbnail) and open state
  const [uploadImageType, setUploadImageType] = useState<ImageTypeEnum | null>(
    null,
  );

  // Feedback modal state
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [selectedLessonForFeedback, setSelectedLessonForFeedback] = useState<{
    lessonId: string;
    lessonTitle: string;
  } | null>(null);

  useEffect(() => {
    setUpdatedModule(originalModuleData);
  }, [originalModuleData]);

  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const mutation = useUpdateTopic({
    onSuccess: async () => {
      notify("success", "Module updated successfully");
      setUpdatedModule(blankModule);
      setIsEdit(false);
      await queryClient.invalidateQueries({ queryKey: ["modules"] });
    },
    onError: async () => {
      notify("error", "There was an error submitting the module.");
    },
  });

  const handleRemoveLesson = (lessonId: string) => {
    const newLessons = updatedModule.lessons.filter(
      (lesson) => lesson._id !== lessonId,
    );
    setUpdatedModule({ ...updatedModule, lessons: newLessons });
  };

  const setStatusColor = (status) => {
    switch (status) {
      case ModuleStatus.Published:
        return "bg-green-100 text-green-600";
      case ModuleStatus.Archived:
        return "bg-gray-100 text-gray-600";
      case ModuleStatus.Coming_Soon:
        return "bg-purple-100 text-purple-300";
      default:
        return "bg-yellow-100 text-yellow-600";
    }
  };

  const handleOpenFeedbackModal = (lessonId: string, lessonTitle: string) => {
    setSelectedLessonForFeedback({ lessonId, lessonTitle });
    setIsFeedbackModalOpen(true);
  };

  const topicDetailsContent = (
    <div className="space-y-2 overflow-y-auto h-full p-1">
      <Input
        label="Title"
        value={updatedModule.title}
        onChange={(e) =>
          setUpdatedModule((prev) => ({
            ...prev,
            title: e.target.value,
          }))
        }
        disabled={!isEdit}
        className="mb-4"
      />
      <div className="mb-4">
        <SlugInputField
          value={updatedModule.slug || ""}
          onChange={(slug) => setUpdatedModule((prev) => ({ ...prev, slug }))}
          onBlur={validateSlug}
          onResetStatus={resetSlugStatus}
          title={updatedModule.title}
          urlPrefix="/topics/"
          slugStatus={slugStatus}
          disabled={!isEdit}
        />
      </div>
      <Input
        label="Subtitle"
        value={updatedModule.subtitle || ""}
        onChange={(e) =>
          setUpdatedModule((prev) => ({
            ...prev,
            subtitle: e.target.value,
          }))
        }
        disabled={!isEdit}
        className="mb-4"
        placeholder="e.g., Harness the Surprising Power of Gratitude"
      />
      <ExternalTextEditor
        label="Module Description"
        value={updatedModule.description}
        handleChange={(e) =>
          setUpdatedModule((prev) => ({ ...prev, description: e }))
        }
        placeholder="Enter the module description here..."
        disabled={!isEdit}
      />
      <MultiSelectDropdown
        label="Categories"
        options={Object.values(CategoryEnum).map((val) => ({
          value: val,
          label: val,
        }))}
        value={updatedModule.categories.map((category) => ({
          value: category,
          label: category,
        }))}
        onChange={(val) => {
          setUpdatedModule((prev) => ({
            ...prev,
            categories: val.map((c) => c.value),
          }));
        }}
        disabled={!isEdit}
      />
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-0">
          <IconSelect
            icon={updatedModule.icon}
            setIcon={(icon) => setUpdatedModule((prev) => ({ ...prev, icon }))}
            disabled={!isEdit}
          />
        </div>
        {isEdit && canCreateContent && (
          <Button
            variant="secondary"
            onClick={() => setUploadImageType(ImageTypeEnum.ICON)}
            title="Upload a new icon"
            className="shrink-0"
          >
            <CloudArrowUpIcon className="w-5 h-5" />
            Upload
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-0">
          <ThumbnailSelect
            thumbnail={updatedModule.thumbnail}
            setThumbnail={(thumbnail) =>
              setUpdatedModule((prev) => ({ ...prev, thumbnail }))
            }
            disabled={!isEdit}
          />
        </div>
        {isEdit && canCreateContent && (
          <Button
            variant="secondary"
            onClick={() => setUploadImageType(ImageTypeEnum.THUMBNAIL)}
            title="Upload a new thumbnail"
            className="shrink-0"
          >
            <CloudArrowUpIcon className="w-5 h-5" />
            Upload
          </Button>
        )}
      </div>
      <ColorSelect
        module={updatedModule}
        setModule={setUpdatedModule}
        disabled={!isEdit}
      />
      <Input
        label="Trailer video link"
        value={updatedModule.trailerVideoUrl || ""}
        onChange={(e) =>
          setUpdatedModule((prev) => ({
            ...prev,
            trailerVideoUrl: e.target.value,
          }))
        }
        disabled={!isEdit}
        className="mb-4"
        placeholder="www.vimeo.com/..."
      />
      <ExternalTextEditor
        label="Trailer transcript"
        value={updatedModule.trailerTranscript || ""}
        handleChange={(value) =>
          setUpdatedModule((prev) => ({ ...prev, trailerTranscript: value }))
        }
        placeholder="Enter transcript here..."
        disabled={!isEdit}
      />
      <div className="flex items-center justify-between p-2 space-x-2">
        <div className="flex-1">
          <Select
            options={[
              { value: ModuleStatus.Draft, label: "Draft" },
              { value: ModuleStatus.Published, label: "Published" },
              { value: ModuleStatus.Archived, label: "Archived" },
              { value: ModuleStatus.Coming_Soon, label: "Coming Soon" },
            ]}
            value={
              updatedModule.status
                ? {
                    value: updatedModule.status,
                    label: updatedModule.status,
                  }
                : {
                    value: null,
                    label: null,
                  }
            }
            onChange={(option: SelectOption<ModuleStatus>) =>
              setUpdatedModule((prev) => ({
                ...prev,
                status: option.value,
              }))
            }
            isDisabled={!isEdit}
          />
        </div>
        <span
          className={`text-sm font-medium px-2 py-1 rounded-md ${setStatusColor(originalModuleData.status)}`}
        >
          {originalModuleData.status || "Unpublished"}
        </span>
      </div>
    </div>
  );

  const lessonsContent = (
    <div className="overflow-y-auto h-full p-1">
      <LessonSelect
        module={updatedModule}
        setModule={setUpdatedModule}
        disabled={!isEdit}
      />
      <ReorderableVerticalList
        items={updatedModule.lessons}
        getId={(lesson) => lesson._id}
        onReorder={(lessons) =>
          setUpdatedModule((prev) => ({ ...prev, lessons }))
        }
        droppableId="edit-topic-lessons"
        isDragDisabled={!isEdit}
        className="space-y-2 mt-2"
      >
        {(lesson, _index, provided) =>
          isEdit ? (
            <RbdFullRowDragSurface provided={provided}>
              <Card
                onClose={() => handleRemoveLesson(lesson._id)}
                disabled={false}
                title={
                  <div className="flex flex-row space-x-2 items-center">
                    <div className="text-md font-semibold">{lesson.title}</div>
                  </div>
                }
              />
            </RbdFullRowDragSurface>
          ) : (
            <LessonCard
              lesson={lesson}
              onOpenFeedbackModal={handleOpenFeedbackModal}
              moduleId={originalModuleData._id}
              provided={provided}
              canUpdateContent={canUpdateContent}
            />
          )
        }
      </ReorderableVerticalList>
    </div>
  );

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
    setSelectedLessonForFeedback(null);
  };

  return (
    <Container className="flex flex-col h-[83vh] relative overflow-hidden">
      {/* Sticky Header with Edit/Save buttons - always visible at top */}
      <header
        className="sticky top-0 z-10 bg-white flex flex-row justify-between items-center pb-3 border-b border-gray-200"
        aria-label="Module editor toolbar"
      >
        <h3 className="mb-0">{updatedModule.title || "Module"}</h3>
        <div className="flex flex-row space-x-2 items-center">
          <BookmarkButton
            module={originalModuleData}
            width={6}
            variant="custom"
          />
          {isEdit ? (
            <>
              <Button
                variant="alternative"
                onClick={() => {
                  const isConfirmed = confirm(
                    "Are you sure you want to proceed?\nAll changes will be lost.",
                  );
                  if (isConfirmed) {
                    setUpdatedModule(originalModuleData);
                    setIsEdit(false);
                  }
                }}
              >
                <span className="text-red-400">Cancel</span>
              </Button>
              <Button
                onClick={() => {
                  if (validateModule(updatedModule))
                    mutation.mutate(updatedModule);
                }}
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="alternative"
              onClick={() => canUpdateContent && setIsEdit(true)}
              disabled={!canUpdateContent}
              title={
                !canUpdateContent
                  ? "Ask an admin if you need permission to edit topics"
                  : "Edit module"
              }
            >
              <PencilSquareIcon className="w-6 h-6" />
            </Button>
          )}
        </div>
      </header>

      {/* Tabs for Topic Details and Lessons */}
      <div className="flex-1 overflow-hidden">
        <TabGroup
          tabs={[
            { label: "Lessons", content: lessonsContent },
            { label: "Topic Details", content: topicDetailsContent },
          ]}
          variant="underline"
        />
      </div>

      {/* Lesson Feedback Modal */}
      {selectedLessonForFeedback && (
        <LessonFeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={handleCloseFeedbackModal}
          lessonId={selectedLessonForFeedback.lessonId}
          lessonTitle={selectedLessonForFeedback.lessonTitle}
        />
      )}

      {/* Upload image from topic details */}
      {uploadImageType !== null && (
        <AddImageModal
          isModalOpen={true}
          setIsModalOpen={(open) => {
            if (!open) setUploadImageType(null);
          }}
          type={uploadImageType}
          showPrefill={true}
          useUploadedImage={(image: ImageI) => {
            if (uploadImageType === ImageTypeEnum.ICON) {
              setUpdatedModule((prev) => ({ ...prev, icon: image }));
            } else if (uploadImageType === ImageTypeEnum.THUMBNAIL) {
              setUpdatedModule((prev) => ({ ...prev, thumbnail: image }));
            }
            setUploadImageType(null);
          }}
        />
      )}
    </Container>
  );
};
export default EditTopicComponent;
